import React, { useCallback, useEffect, useRef, useState } from "react";
import Card from "components/card";

import {
  createColumnHelper,
  flexRender,
  getCoreRowModel,
  getSortedRowModel,
  useReactTable,
} from "@tanstack/react-table";
import { IoEye } from "react-icons/io5";
import { useNavigate } from "react-router-dom";
import Pagination from "components/pagination/Pagination";
import Loader from "../../../assets/img/loader.gif";
import Toast from "components/toaster";
import moment from "moment";
import { FiSearch } from "react-icons/fi";
import Select from "react-select";
import UserService from "services/user.service";
import { FaTrash } from "react-icons/fa6";
import { MdDeleteForever } from "react-icons/md";
import tabCache from "utils/tabCache";

const columnHelper = createColumnHelper();

const Contacts = () => {
  const activateRef = useRef();
  const prevSearchTextRef = useRef("");
  const navigate = useNavigate();
  const [sorting, setSorting] = React.useState([]);
  // Initialize from cache if available
  const [data, setData] = useState(tabCache.contacts.data || []);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [totalPages, setTotalPages] = useState(1);
  // Initialize from cache - show loader only on first load
  const [isLoading, setIsLoading] = useState(false);
  const [reportType, setReportType] = useState("");
  const [searchText, setSearchText] = useState("");
  const [searchInputValue, setSearchInputValue] = useState("");
  const [viewOpen, setViewOpen] = useState(null);
  const [selectedUser, setSelectedUser] = useState(null);
  const [isViewLoading, setIsViewLoading] = useState(false);
  const [popupOpenDelete, setPopupOpenDelete] = useState(false);
  const [id, setId] = useState("");
  const [confirmLoader, setConfirmLoader] = useState(false);
  const [isPageChanging, setIsPageChanging] = useState(false);


  const getContact = async (isSearching = false) => {
    const isFirstLoad = tabCache.contacts.firstLoad;
    
    // Show loader on first load or when searching
    if (isFirstLoad || isSearching || isPageChanging) {
      setIsLoading(true);
    }
    
    
    try {
      const body = {
        pagination: {
          page: page,
          pageSize: pageSize,
        },
        searchtext: encodeURIComponent(searchText),
      };
      const response = await UserService.getContacts(body);
      const contactsData = response.data.data;
      
      // Update state and cache
      setData(contactsData);
      tabCache.contacts.data = contactsData;
      setTotalPages(Math.round(response.data.totalCount / pageSize));
      console.log("🚀 ~ getContact ~ response.data.data:", contactsData);
      
      // After first API call, don't show loader next time
      if (isFirstLoad) {
        tabCache.contacts.firstLoad = false;
      }
    } catch (error) {
      console.log("🚀 ~ getContact ~ error:", error);
      Toast("error", error.response.data.message);
      const emptyData = [];
      setData(emptyData);
      tabCache.contacts.data = emptyData;
      if (isFirstLoad) {
        tabCache.contacts.firstLoad = false;
      }
    } finally {
      if (isFirstLoad || isSearching || isPageChanging) {
        setIsLoading(false);
      }
      setIsPageChanging(false); // 👈 STOP pagination loader
    }
    
  };

  const debounce = (func, delay) => {
    let timeoutId;
    return function (...args) {
      if (timeoutId) clearTimeout(timeoutId);
      timeoutId = setTimeout(() => {
        func.apply(this, args);
      }, delay);
    };
  };

  // Create debounced search handler
  const debouncedSearch = useCallback(
    debounce((value) => {
      setSearchText(value);
    }, 500),
    []
  );

  useEffect(() => {
    // Check if searchText changed (this indicates a search operation)
    const isSearchChange = prevSearchTextRef.current !== searchText;
    if (isSearchChange) {
      prevSearchTextRef.current = searchText;
    }
    
    // If we have cached data, show it immediately and fetch in background
    if (tabCache.contacts.data) {
      // Data already shown from initial state, just fetch in background
      // Show loader if this is a search operation
      getContact(isSearchChange);
    } else {
      // No cached data, fetch and show loader
      getContact(false);
    }
    // eslint-disable-next-line
  }, [page, pageSize, searchText, reportType]);

  const handleSearch = (e) => {
    const value = e.target.value;
    setSearchInputValue(value);
    debouncedSearch(value);
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (activateRef.current && !activateRef.current.contains(event.target)) {
        setPopupOpenDelete(false);
        document.body.style.overflow = "unset";
      }
    };

    document.addEventListener("mousedown", handleClickOutside);

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const columns = [
    columnHelper.accessor("name", {
      id: "name",
      header: () => (
        <p className="text-sm font-bold uppercase text-white">Time</p>
      ),
      cell: (info) => (
        <div className="flex items-center">
          <p className="text-sm font-bold text-white">
            {info.getValue() || "-"}
          </p>
        </div>
      ),
    }),
    columnHelper.accessor("email", {
      id: "email",
      header: () => (
        <p className="text-sm font-bold uppercase text-white">Reported by</p>
      ),
      cell: (info) => (
        <p className="text-sm font-bold text-white">
          {info.getValue() || "--"}
        </p>
      ),
    }),
    columnHelper.accessor("message", {
      id: "message",
      header: () => (
        <p className="text-sm font-bold uppercase text-white">Message</p>
      ),
      cell: (info) => (
        <p className="text-sm font-bold text-white">
          {info.getValue() ? info.getValue() : "-"}
        </p>
      ),
    }),
    columnHelper.accessor("phone", {
      id: "phone",
      header: () => (
        <p className="text-sm font-bold uppercase text-white text-start">Phonenumber</p>
      ),
      cell: (info) => {
        const rowId = info.row.original._id;
        return (
          <div className="flex items-center justify-start gap-3 ">
            <span className="text-left">{info.getValue() || "--"}</span>
            <div
              className="ms-center flex h-8 w-8 cursor-pointer items-center justify-center rounded-lg bg-[rgba(255,37,37,0.1)] text-[rgba(255,44,57,1)] opacity-0 group-hover:opacity-100 transition-opacity duration-150"
              onClick={() =>
                handleOpenDelete(rowId, info.getValue(), info.row.original)
              }
            >
              {isViewLoading && rowId === id ? (
                <img src={Loader} alt="Loading..." className="mx-auto h-4 w-4" />
              ) : (
                <MdDeleteForever size={20}  />
              )}
            </div>
          </div>
        );
      },
    }),
  ]; // eslint-disable-next-line

  const handleOpenDelete = async (id, active, data) => {
    console.log("🚀 ~ handleOpenDelete ~ id:", id);
    setSelectedUser(data);
    setId(id);
    setPopupOpenDelete(true);
    document.body.style.overflow = "hidden";
  };

  const handleOpen = async (item) => {
    setViewOpen(item);
    document.body.style.overflow = "hidden";
  };

  const handleClose = () => {
    setViewOpen(null);
    document.body.style.overflow = "unset";
  };

  // const [data, setData] = React.useState(() => [...defaultData]);
  const table = useReactTable({
    data,
    columns,
    state: {
      sorting,
    },
    onSortingChange: setSorting,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    debugTable: true,
  });

  const handlePageChange = (newPage) => {
    setIsPageChanging(true);   // 👈 START loader for pagination
    setPage(newPage);
  };
  

  const options = [
    { label: "All", value: "" },
    { label: "Post", value: "post" },
    { label: "Account", value: "account" },
  ];

  const handleSelectReportType = async (option) => {
    setReportType(option.value);
  };

  const colourStyles = {
    option: (styles) => {
      return {
        ...styles,
        backgroundColor: "rgb(30 30 32 / 1) !important",
        ":hover": {
          backgroundColor: "rgba(255, 255, 255, 0.30) !important",
          color: "#ffffff",
        },
      };
    },
  };

  const handleDelete = async () => {
    setConfirmLoader(true);
    try {
      const body = {
        contact_id: id,
      };
      const response = await UserService.deleteContact(body);
      setPopupOpenDelete(false);
      document.body.style.overflow = "unset";
      getContact();
      Toast("success", response.data.message);
    } catch (error) {
      Toast("error", error.response.data.message);
    } finally {
      setConfirmLoader(false);
    }
  };
  // Get all requests

  // ========
  // curl --location 'https://api.car-hangout.com/api/v1/admin/contactus' \
  // --header 'Content-Type: application/json' \
  // --header 'Authorization: Bearer eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJ1c2VyX2lkIjoiNjc2ZDhjYjU1ZTY0YjEzYmRjM2ExYzk0IiwiYWNjb3VudF9pZCI6IjY3ZjBjZTlkNzQ5M2I3NWFlNTI4ZDViOSIsInBob25lIjoiKzkxMTExMTExMTExMSIsImlhdCI6MTc0NjExNDY2NywiZXhwIjoxNzQ2MjAxMDY3fQ.2EaHYDzK8ZAYA3SnqC-fEmjrfZ9XxJLihFEVihZaXKc' \
  // --data '{
  //     "pagination": {
  //         "page": 1,
  //         "pageSize": 20
  //     },
  //     "searchtext": ""
  // }'

  //   curl --location 'https://api.car-hangout.com/api/v1/admin/contactus/delete' \
  // --header 'Content-Type: application/json' \
  // --header 'Authorization: Bearer eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJ1c2VyX2lkIjoiNjc2ZDhjYjU1ZTY0YjEzYmRjM2ExYzk0IiwiYWNjb3VudF9pZCI6IjY3ZjBjZTlkNzQ5M2I3NWFlNTI4ZDViOSIsInBob25lIjoiKzkxMTExMTExMTExMSIsImlhdCI6MTc0NjExNDY2NywiZXhwIjoxNzQ2MjAxMDY3fQ.2EaHYDzK8ZAYA3SnqC-fEmjrfZ9XxJLihFEVihZaXKc' \
  // --data '{
  //     "contact_id":"681397dd0cec7dde15cca988"
  // }'
  return (
    <div className="-mt-1 grid h-full grid-cols-1 gap-5">
      <div className="col-span-1 h-full w-full rounded-xl 2xl:col-span-1">
        <Card extra={"w-full h-fit pb-6 px-6 relative mt-4"}>
          <div className="reports-header-container flex w-full flex-col gap-3 sm:flex-row sm:items-center sm:justify-between sm:gap-4 mt-4 -ml-2">
            <div className="flex w-full items-center sm:w-auto">
              <div className="search-input-container flex items-center rounded-full bg-navy-900 py-3 text-white w-[180px]">
                <p className="pl-3 pr-2 text-xl">
                  <FiSearch className="h-4 w-4 text-white" />
                </p>
                <input
                  type="text"
                  placeholder="Search..."
                  className="search-input-field block w-full flex-1 bg-navy-900 text-sm font-medium text-white outline-none placeholder:!text-white"
                  onChange={handleSearch}
                  value={searchInputValue}
                />
              </div>
            </div>
          </div>
          <div
            className={`table-scroll mt-6 -mx-6 relative ${
              isLoading
                ? "h-[65vh] overflow-hidden"
                : "overflow-x-auto"
            }`}
          >
            {isLoading && (
              <div className="pointer-events-none absolute inset-0 z-40 hidden items-center justify-center md:flex">
                <img src={Loader} alt="Loading..." className="h-16 w-16" />
              </div>
            )}
            <table
              className={`z-10 w-full ${isLoading ? "table-fixed" : ""}`}
              style={
                isLoading
                  ? { tableLayout: "fixed", width: "100%" }
                  : { tableLayout: "auto", minWidth: "100%" }
              }
            >
              <thead>
                {!isLoading &&
                  table.getHeaderGroups().map((headerGroup) => (
                    <tr
                      key={headerGroup.id}
                      className="!border-px !border-gray-400"
                    >
                      {headerGroup.headers.map((header, index) => {
                        const isFirst = index === 0;
                        const isLast = index === headerGroup.headers.length - 1;
                        const isName = header.id === 'name';
                        const isPhone = header.id === 'phone';
                        return (
                          <th
                            key={header.id}
                            colSpan={header.colSpan}
                            onClick={header.column.getToggleSortingHandler()}
                            className={`cursor-pointer border-b-[1px] border-[#fbfbfb1a] pb-2 pt-4 ${isFirst ? 'pl-6 pr-12 text-start' : isPhone ? 'pl-12 pr-36 text-start' : isLast ? 'pl-12 pr-36 text-end' : isName ? 'pl-24 pr-12 text-start' : 'pl-14 pr-12 text-start'}`}
                          >
                            <div className="items-center justify-between text-xs text-gray-200">
                              {flexRender(
                                header.column.columnDef.header,
                                header.getContext()
                              )}
                              {{
                                asc: "",
                                desc: "",
                              }[header.column.getIsSorted()] ?? null}
                            </div>
                          </th>
                        );
                      })}
                    </tr>
                  ))}
              </thead>
              <tbody>
                {isLoading ? (
                  <tr className="md:hidden">
                    <td colSpan={columns.length} className="py-6">
                      <div className="flex justify-center">
                        <img src={Loader} alt="Loading..." className="h-16 w-16" />
                      </div>
                    </td>
                  </tr>
                ) : table.getRowModel().rows?.length === 0 ? (
                  <tr>
                    <td colSpan={columns.length} className="py-4 text-center">
                      No data found
                    </td>
                  </tr>
                ) : (
                  table.getRowModel().rows.map((row) => {
                    return (
                      <tr key={row.id} className="group border-b border-[#fbfbfb1a] last:border-none hover:bg-[#8E8E8E]/5 hover:!border-b-0 transition-colors cursor-pointer">
                        {row.getVisibleCells().map((cell, cellIndex) => {
                          const isFirst = cellIndex === 0;
                          const isLast = cellIndex === row.getVisibleCells().length - 1;
                          const isName = cell.column.id === 'name';
                          return (
                            <td
                              key={cell.id}
                              className={`relative min-w-[150px] border-white/0 py-3 ${isFirst ? 'pl-6 pr-12' : isLast ? 'pl-12 pr-32' : isName ? 'pl-24 pr-24' : 'pl-14 pr-24'}`}
                            >
                              {flexRender(
                                cell.column.columnDef.cell,
                                cell.getContext()
                              )}
                            </td>
                          );
                        })}
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>

          {table.getRowModel().rows.length > 0 && (
            <Pagination
              page={page}
              totalPages={totalPages}
              handlePageChange={handlePageChange}
            />
          )}
        </Card>
      </div>

      {popupOpenDelete && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-70">
          <div
            className="w-96 rounded-xl bg-navy-800 p-6 shadow-lg"
            ref={activateRef}
          >
            <h2 className="mb-4 text-xl font-bold text-white">Delete</h2>
            <p className="mb-6 text-gray-600">
              Are you sure you want to delete this contact detail?
            </p>
            <div className="flex justify-end space-x-4">
              <button
                onClick={() => {
                  setPopupOpenDelete(false);
                  document.body.style.overflow = "unset";
                }}
                className="px-5 py-2.5 rounded-lg bg-white/10 text-white hover:bg-white/20 transition-all duration-200 font-semibold text-sm border border-white/20"
              >
                Cancel
              </button>
              <button
                onClick={() => {
                  handleDelete();
                }}
                className="w-24 rounded-md bg-orangePrimary px-4 py-2 text-white hover:opacity-90"
              >
                {confirmLoader ? (
                  <img
                    src={Loader}
                    alt="Loading..."
                    className="mx-auto h-6 w-6"
                  />
                ) : (
                  "Delete"
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Contacts;
